import {useMemo, useState} from "react";
import {loadStripe} from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {Box, Button, Text} from "@chakra-ui/react";

// Embedded Stripe card capture driven by a SetupIntent client_secret.
// Mirrors the SetupForm of the clinic /payment-setup page (different app, so
// reimplemented here). It NEVER sees a session token: the clientSecret + key
// are produced by the backend after validating the form link. On success it
// hands the payment method id back via onConfirm — the parent calls the
// confirm endpoint and records only {saved,last4,brand}.

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#0570de",
    colorBackground: "#ffffff",
    colorText: "#1a202c",
    colorDanger: "#df1b41",
    borderRadius: "8px",
  },
};

function SetupForm({onConfirm}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const {error: confirmError, setupIntent} = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (confirmError) {
        setError(confirmError.message || "Could not save the card.");
        setSubmitting(false);
        return;
      }
      const paymentMethodId = setupIntent?.payment_method;
      if (!paymentMethodId || setupIntent?.status !== "succeeded") {
        setError("The card could not be confirmed. Please try again.");
        setSubmitting(false);
        return;
      }
      await onConfirm(paymentMethodId);
      // parent unmounts/replaces this on success; keep submitting state on
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e?.message ||
          "Something went wrong saving your card."
      );
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        options={{wallets: {applePay: "never", googlePay: "never"}}}
      />
      {error ? (
        <Text mt={2} fontSize="sm" color="red.500">
          {error}
        </Text>
      ) : null}
      <Button
        type="submit"
        w="full"
        mt={4}
        bg="gray.900"
        color="white"
        _hover={{bg: "gray.800"}}
        _active={{bg: "gray.700"}}
        isLoading={submitting}
        isDisabled={!stripe || !elements}
      >
        Save payment method
      </Button>
    </form>
  );
}

export default function EmbeddedCardSetup({clientSecret, stripePublicKey, onConfirm}) {
  const stripePromise = useMemo(
    () =>
      stripePublicKey && /^(pk_live_|pk_test_)/.test(stripePublicKey)
        ? loadStripe(stripePublicKey)
        : null,
    [stripePublicKey]
  );

  if (!stripePromise || !clientSecret) {
    return (
      <Box p={3} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
        <Text fontSize="sm" color="red.500">
          Payment is not available right now. Please contact the clinic.
        </Text>
      </Box>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{clientSecret, appearance, locale: "en"}}>
      <SetupForm onConfirm={onConfirm} />
    </Elements>
  );
}
